/**
 * Native HTML5 drag-and-drop hook for Kanban cards.
 * Usage:
 *   const dnd = useDragKanban((cardId, fromStage, toStage) => api.patch(...));
 *   <div {...dnd.column(stage.id)}>
 *     <div {...dnd.card(card.id, stage.id)}>...</div>
 *   </div>
 */
import { useState, useRef } from "react";

export function useDragKanban(onDrop) {
  const [dragging, setDragging] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const dragRef = useRef(null);

  const card = (cardId, fromStage) => ({
    draggable: true,
    onDragStart: (e) => {
      dragRef.current = { cardId, fromStage };
      setDragging(cardId);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", cardId);
    },
    onDragEnd: () => {
      setDragging(null);
      setOverStage(null);
      dragRef.current = null;
    },
    style: { opacity: dragging === cardId ? 0.4 : 1, cursor: "grab" },
  });

  const column = (stageId) => ({
    onDragOver: (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (overStage !== stageId) setOverStage(stageId);
    },
    onDragLeave: (e) => {
      if (e.currentTarget.contains(e.relatedTarget)) return;
      setOverStage((s) => (s === stageId ? null : s));
    },
    onDrop: (e) => {
      e.preventDefault();
      const data = dragRef.current;
      if (data && data.fromStage !== stageId) {
        onDrop(data.cardId, data.fromStage, stageId);
      }
      setOverStage(null);
      setDragging(null);
    },
    "data-drop-active": overStage === stageId,
  });

  return { card, column, dragging, overStage };
}
